import "@/App.css";
import { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TbmProvider } from "@/context/TbmContext";
import { LangProvider } from "@/context/LangContext";
import Layout from "@/components/Layout";
import RiggingLayout from "@/components/RiggingLayout";
import Login from "@/pages/Login";
import { lazyWithPreload } from "@/lib/lazyPreload";
import { preloadAllRoutes } from "@/lib/routePreload";

const Register = lazyWithPreload(() => import("@/pages/Register"));
const Dashboard = lazyWithPreload(() => import("@/pages/Dashboard"));
const DailyLogs = lazyWithPreload(() => import("@/pages/DailyLogs"));
const DailyLogNew = lazyWithPreload(() => import("@/pages/DailyLogNew"));
const DailyLogDetail = lazyWithPreload(() => import("@/pages/DailyLogDetail"));
const Equipment = lazyWithPreload(() => import("@/pages/Equipment"));
const EquipmentDetail = lazyWithPreload(() => import("@/pages/EquipmentDetail"));
const JobSitePictures = lazyWithPreload(() => import("@/pages/JobSitePictures"));
const Checklists = lazyWithPreload(() => import("@/pages/Checklists"));
const ChecklistRun = lazyWithPreload(() => import("@/pages/ChecklistRun"));
const ChecklistRecords = lazyWithPreload(() => import("@/pages/ChecklistRecords"));
const ChecklistRecord = lazyWithPreload(() => import("@/pages/ChecklistRecord"));
const Consumption = lazyWithPreload(() => import("@/pages/Consumption"));
const Parts = lazyWithPreload(() => import("@/pages/Parts"));
const Stock = lazyWithPreload(() => import("@/pages/Stock"));
const Health = lazyWithPreload(() => import("@/pages/Health"));
const HealthLogs = lazyWithPreload(() => import("@/pages/HealthLogs"));
const DailyReport = lazyWithPreload(() => import("@/pages/DailyReport"));
const DailyReports = lazyWithPreload(() => import("@/pages/DailyReports"));
const CalendarPage = lazyWithPreload(() => import("@/pages/Calendar"));
const Documents = lazyWithPreload(() => import("@/pages/Documents"));
const Settings = lazyWithPreload(() => import("@/pages/Settings"));
const Master = lazyWithPreload(() => import("@/pages/Master"));
const RiggingDashboard = lazyWithPreload(() => import("@/pages/RiggingDashboard"));
const RiggingInventory = lazyWithPreload(() => import("@/pages/RiggingInventory"));
const RiggingInspections = lazyWithPreload(() => import("@/pages/RiggingInspections"));
const RiggingQuarterly = lazyWithPreload(() => import("@/pages/RiggingQuarterly"));
const RiggingQuarantine = lazyWithPreload(() => import("@/pages/RiggingQuarantine"));
const RiggingReports = lazyWithPreload(() => import("@/pages/RiggingReports"));
const RiggingCostTable = lazyWithPreload(() => import("@/pages/RiggingCostTable"));

// Warm every route chunk after the app is idle so page switches are instant.
function ChunkWarmer() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 400));
    const id = idle(() => preloadAllRoutes());
    return () => (window.cancelIdleCallback || clearTimeout)(id);
  }, [user]);
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-surf" />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Layout>{children}</Layout>;
}

function RiggingProtected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-surf" />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <RiggingLayout>{children}</RiggingLayout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-surf" />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App min-h-screen bg-surf">
      <BrowserRouter>
        <AuthProvider>
          <LangProvider>
            <TbmProvider>
              <ChunkWarmer />
              <Suspense fallback={<div className="min-h-screen bg-surf" />}>
                <Routes>
                <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
                <Route path="/" element={<Protected><Dashboard /></Protected>} />
                <Route path="/daily-logs" element={<Protected><DailyLogs /></Protected>} />
                <Route path="/daily-logs/new" element={<Protected><DailyLogNew /></Protected>} />
                <Route path="/daily-logs/:id" element={<Protected><DailyLogDetail /></Protected>} />
                <Route path="/equipment" element={<Protected><Equipment /></Protected>} />
                <Route path="/equipment/:id" element={<Protected><EquipmentDetail /></Protected>} />
                <Route path="/checklists" element={<Protected><Checklists /></Protected>} />
                <Route path="/checklist/:id" element={<Protected><ChecklistRun /></Protected>} />
                <Route path="/checklist-records" element={<Protected><ChecklistRecords /></Protected>} />
                <Route path="/checklist-record/:id" element={<Protected><ChecklistRecord /></Protected>} />
                <Route path="/consumption" element={<Protected><Consumption /></Protected>} />
                <Route path="/parts" element={<Protected><Parts /></Protected>} />
                <Route path="/stock" element={<Protected><Stock /></Protected>} />
                <Route path="/health" element={<Protected><Health /></Protected>} />
                <Route path="/health-logs" element={<Protected><HealthLogs /></Protected>} />
                <Route path="/daily-report" element={<Protected><DailyReport /></Protected>} />
                <Route path="/daily-reports" element={<Protected><DailyReports /></Protected>} />
                <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
                <Route path="/documents" element={<Protected><Documents /></Protected>} />
                <Route path="/job-site-pictures" element={<Protected><JobSitePictures /></Protected>} />
                <Route path="/settings" element={<Protected><Settings /></Protected>} />
                <Route path="/master" element={<Protected><Master /></Protected>} />
                <Route path="/rigging" element={<RiggingProtected><RiggingDashboard /></RiggingProtected>} />
                <Route path="/rigging/inventory" element={<RiggingProtected><RiggingInventory /></RiggingProtected>} />
                <Route path="/rigging/inspections" element={<RiggingProtected><RiggingInspections /></RiggingProtected>} />
                <Route path="/rigging/quarterly" element={<RiggingProtected><RiggingQuarterly /></RiggingProtected>} />
                <Route path="/rigging/quarantine" element={<RiggingProtected><RiggingQuarantine /></RiggingProtected>} />
                <Route path="/rigging/reports" element={<RiggingProtected><RiggingReports /></RiggingProtected>} />
                <Route path="/rigging/cost-table" element={<RiggingProtected><RiggingCostTable /></RiggingProtected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
              <Toaster position="top-right" richColors />
            </TbmProvider>
          </LangProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
